import React from 'react';
import { observer } from 'mobx-react-lite';
import { useAppStateContext } from 'src/AppState/AppState';
import {
    ErrorMessageBar,
    InputError,
    InputLabel,
} from 'src/domains/layouts/shared/formComponents/FormComponents.style';
import {
    FormComponent,
    FormWrapper,
    DatesWrapper,
    DateInputWrapper,
} from 'src/domains/layouts/shared/cms/form/Form.style';
import { Label } from 'src/domains/layouts/shared/typography/Content.style';
import { UploadImg } from 'src/components/UploadImg/UploadImg';
import DatePicker from 'react-datepicker';
import { LiveNotificationsState } from 'src/AppState/LiveNotificationsState/LiveNotificationsState';
import { observable, makeAutoObservable } from 'mobx';
import { numberOfApperanceOptions } from 'src/components/LiveNotificationsPage/selectionOptions';
import {
    ActiveLabel,
    CheckboxWrapper,
} from 'src/components/LiveNotificationsPage/LiveNotificationsListItem/LiveNotificationListItem.style';
import { FormHeader } from 'src/domains/layouts/shared/cms/form/formHeader/FormHeader';
import { FormButtons } from 'src/domains/layouts/shared/buttons/FormButtons';
import { Loader } from 'src/domains/layouts/shared/loader/Loader';
import { Input } from 'src/domains/layouts/shared/formComponents/input/Input';
import { Textarea } from 'src/domains/layouts/shared/formComponents/textarea/Textarea';
import { Select } from 'src/domains/layouts/shared/formComponents/Select/components/Select';

class State {
    public constructor(private readonly liveNotificationsState: LiveNotificationsState) {
        makeAutoObservable(this);
    }
    @observable public startDateError = false;
    @observable public endDateError = false;
    @observable public showDirectLinkField = false;

    public onChangeStartDate = (date: Date | null): void => {
        if (date !== null) {
            this.liveNotificationsState.dateFrom.setValue(date.toISOString());
        }
    };

    public onBlurStartDate = (date: React.FocusEvent<HTMLInputElement>): void => {
        if (date.target.value === '') {
            this.startDateError = true;
        } else if (this.startDateError) {
            this.startDateError = false;
        }
    };

    public onChangeEndDate = (date: Date | null): void => {
        if (date !== null) {
            this.liveNotificationsState.dateTo.setValue(date.toISOString());
        }
    };

    public setShowForAll = (): void => {
        this.liveNotificationsState.setShowForAll(!this.liveNotificationsState.showForAll);
    };

    public onBlurEndDate = (date: React.FocusEvent<HTMLInputElement>): void => {
        if (date.target.value === '') {
            this.endDateError = true;
        } else if (this.endDateError) {
            this.endDateError = false;
        }
    };
}

export const LiveNotificationForm = observer(() => {
    const { liveNotificationsState } = useAppStateContext();
    const {
        isLoading,
        isFormValid,
        isEditMode,
        createLiveNotification,
        updateLiveNotification,
        resetForm,
        uploadImageState,
        dateFrom,
        dateTo,
        title,
        message,
        ctaText,
        idsList,
        ctaHtmlLinkUrl,
        ctaGameLink,
        ctaEventId,
        visibilityOption,
        addedIds,
        ctaError,
        showForAll,
    } = liveNotificationsState;

    const { configState, appCasinoState } = useAppStateContext();
    const { gamesOptionsForPromos } = appCasinoState.gameStore;

    const isConfigDefined = configState !== null && configState.config !== undefined && configState.config !== null;
    const gamesList =
        isConfigDefined && configState?.config.golfOnVirtuals === true
            ? gamesOptionsForPromos
            : gamesOptionsForPromos.filter((game) => game.name !== 'Virtual Golf');
    const parsedStartDate = dateFrom.value === null ? null : Date.parse(dateFrom.value);
    const startDate = parsedStartDate === null || isNaN(parsedStartDate) ? null : new Date(parsedStartDate);
    const parsedEndDate = dateTo.value === null ? null : Date.parse(dateTo.value);
    const endDate = parsedEndDate === null || isNaN(parsedEndDate) ? null : new Date(parsedEndDate);
    const [state] = React.useState(() => new State(liveNotificationsState));

    const listOfGamesOptions = [{ key: 'none', name: 'None' }, ...gamesList];

    return (
        <FormWrapper>
            <FormHeader
                isEditMode={isEditMode}
                defaultLabel='Create new notification'
                editModeLabel='Edit notification'
                resetForm={resetForm}
            />

            <FormComponent onSubmit={(event): void => event.preventDefault()}>
                <Loader isActive={isLoading} />

                {ctaError === null ? null : <ErrorMessageBar>{ctaError}</ErrorMessageBar>}

                <Input
                    label='Notification name (max 30 Characters) *'
                    formInputState={title}
                    dataTest='set-notification-title'
                />

                <Textarea
                    label='Message (max 200 characters)'
                    formState={message}
                    dataTest='set-notification-message'
                />

                <Input
                    label='Call to Action Button Text'
                    formInputState={ctaText}
                    dataTest='set-cta-button-text'
                />

                <Select
                    selectOptions={listOfGamesOptions}
                    stateValue={ctaGameLink}
                    defaultText='Select Link'
                    label='Call to Action Game Link'
                    dataTest='select-game-link'
                />

                <Input
                    label='Or Call to Action HTML Link'
                    formInputState={ctaHtmlLinkUrl}
                />

                <Input
                    label='Or Event Id'
                    formInputState={ctaEventId}
                    dataTest='set-event-id'
                />

                <UploadImg
                    title='Choose artwork for notification icon (32px height)'
                    uploadImageState={uploadImageState}
                />

                <DatesWrapper>
                    <DateInputWrapper>
                        <InputLabel>Start date *</InputLabel>
                        <DatePicker
                            selected={startDate}
                            onChange={state.onChangeStartDate}
                            onBlur={state.onBlurStartDate}
                            showTimeSelect
                            timeFormat='HH:mm'
                            timeIntervals={15}
                            timeCaption='time'
                            dateFormat='MMMM d, yyyy h:mm aa'
                            id='Start-date'
                        />
                        {state.startDateError ? <InputError>This field is required.</InputError> : null}
                    </DateInputWrapper>
                    <DateInputWrapper>
                        <InputLabel>End date *</InputLabel>
                        <DatePicker
                            selected={endDate}
                            onChange={state.onChangeEndDate}
                            onBlur={state.onBlurEndDate}
                            showTimeSelect
                            timeFormat='HH:mm'
                            timeIntervals={15}
                            timeCaption='time'
                            dateFormat='MMMM d, yyyy h:mm aa'
                            id='End-Date'
                        />
                        {state.endDateError ? <InputError>This field is required.</InputError> : null}
                    </DateInputWrapper>
                </DatesWrapper>

                <Select
                    selectOptions={numberOfApperanceOptions}
                    stateValue={visibilityOption}
                    defaultText='Select number of times users see notification link'
                    label='Number of times users see notification*'
                    dataTest='times-user-see-ln'
                />

                <CheckboxWrapper
                    labelSide='right'
                    onChange={state.setShowForAll}
                    isChecked={showForAll}
                    isDisabled={false}
                    dataTest='active-for-box'
                >
                    <ActiveLabel>All authorized customers</ActiveLabel>
                </CheckboxWrapper>

                <Label>Or manually add users to this promotion (use comma to separate user ids)</Label>

                <Textarea
                    placeholder='Use comma or line break to separate user ids'
                    formState={idsList}
                />

                <Label>Total Customers Added: {addedIds.length}</Label>

                <FormButtons
                    onCreate={createLiveNotification}
                    onUpdate={updateLiveNotification}
                    onCancel={resetForm}
                    isEditMode={isEditMode}
                    isCreateDisabled={!isFormValid}
                    isUpdateDisabled={!isFormValid}
                    dataTestCreate='create-notification-button'
                    dataTestUpdate='create-notification-button'
                />
            </FormComponent>
        </FormWrapper>
    );
});
